import { useState, useEffect } from "react";
import { Loader2, X, Calendar, Clock, Mail, Phone, MapPin, MessageSquare, Trash2, Pencil, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { SmsReplyModal } from "./SmsReplyModal";
import { Timeline } from "./Timeline";
import { CloseTrialModal, OutcomeSummary } from "./CloseTrialModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const TYPE_OPTIONS = [
  { value: "pickup", label: "Pick-up" },
  { value: "dropoff", label: "Drop-off" },
  { value: "delivery", label: "Delivery" },
  { value: "trial", label: "Trial" },
];
const STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-show" },
  // Section 8 — Trial closure outcomes
  { value: "converted", label: "Converted" },
  { value: "follow_up", label: "Follow up" },
  { value: "closed", label: "Closed" },
  { value: "customer_cancelled", label: "Customer cancelled" },
  { value: "admin_cancelled", label: "Admin cancelled" },
  { value: "rescheduled", label: "Rescheduled" },
];

function fullTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function formatTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

function StatusPill({ status }) {
  const cls = status === "confirmed" ? "bg-green-50 text-green-600"
    : status === "completed" ? "bg-blue-50 text-blue-600"
    : status === "converted" ? "bg-emerald-50 text-emerald-700"
    : status === "follow_up" ? "bg-amber-50 text-amber-700"
    : status === "closed" ? "bg-slate-100 text-slate-600"
    : status === "cancelled" || status === "customer_cancelled" || status === "admin_cancelled" ? "bg-red-50 text-red-500"
    : status === "no_show" ? "bg-gray-100 text-gray-500"
    : status === "rescheduled" ? "bg-indigo-50 text-indigo-600"
    : "bg-amber-50 text-amber-600";
  return <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${cls}`}>{(status || "—").replace("_", " ")}</span>;
}

function EditPanel({ appt, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    date: appt.date || "",
    time: appt.time || "",
    appointment_type: appt.appointment_type || "pickup",
    status: appt.status || "confirmed",
    customer_name: appt.customer_name || "",
    customer_email: appt.customer_email || "",
    customer_phone: appt.customer_phone || "",
    delivery_address: appt.delivery_address || "",
    notes: appt.notes || "",
    skip_overlap_check: false,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  return (
    <div className="space-y-4" data-testid="appt-edit-panel">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Type</label>
          <select value={form.appointment_type} onChange={set("appointment_type")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-type">
            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Status</label>
          <select value={form.status} onChange={set("status")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-status">
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Date</label>
          <input type="date" value={form.date} onChange={set("date")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-date" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Time</label>
          <input type="time" value={form.time} onChange={set("time")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-time" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Customer name</label>
          <input value={form.customer_name} onChange={set("customer_name")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-cname" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Email</label>
          <input value={form.customer_email} onChange={set("customer_email")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-email" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Phone</label>
          <input value={form.customer_phone} onChange={set("customer_phone")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-phone" />
        </div>
        {form.appointment_type === "delivery" && (
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Delivery address</label>
            <input value={form.delivery_address} onChange={set("delivery_address")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px]" data-testid="edit-address" />
          </div>
        )}
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Customer-facing notes</label>
          <textarea rows={2} value={form.notes} onChange={set("notes")} className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] resize-none" data-testid="edit-notes" />
        </div>
        <label className="col-span-2 flex items-center gap-2 text-[11px] text-[#676664]/80">
          <input type="checkbox" checked={form.skip_overlap_check} onChange={(e) => setForm({ ...form, skip_overlap_check: e.target.checked })} data-testid="edit-skip-overlap" />
          Skip overlap check (force save even if time conflicts)
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-[#282828]/8">
        <Button variant="outline" onClick={onCancel} disabled={saving} className="text-[11px] h-8 px-4" data-testid="edit-cancel">Cancel</Button>
        <Button onClick={() => onSave(form)} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="edit-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save changes
        </Button>
      </div>
    </div>
  );
}

function NotesTab({ appt, onChanged }) {
  const [notes, setNotes] = useState(appt?.admin_notes || []);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { setNotes(appt?.admin_notes || []); }, [appt?.admin_notes, appt?.id]);

  const add = async () => {
    const t = newNote.trim();
    if (!t) return;
    if (t.length > 2000) { toast.error("Note too long"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/appointments/${appt.id}/notes`, { note: t }, { headers: auth() });
      setNotes([...notes, r.data.note]);
      setNewNote("");
      toast.success("Note added");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add note");
    }
    setSubmitting(false);
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    setDeletingId(noteId);
    try {
      await axios.delete(`${API}/admin/appointments/${appt.id}/notes/${noteId}`, { headers: auth() });
      setNotes(notes.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
    setDeletingId(null);
  };

  return (
    <div data-testid="appt-notes-panel">
      <div className="mb-4">
        <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1.5">Add internal note (admin-only)</label>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Notes are never visible to customers."
          className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none"
          data-testid="appt-new-note-input"
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-[10px] text-[#676664]/40">{newNote.length}/2000</div>
          <Button onClick={add} disabled={submitting || !newNote.trim()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="appt-add-note-btn">
            {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : "+ Add note"}
          </Button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="text-center py-8 text-[12px] text-[#676664]/50 italic">
          <MessageSquare className="w-5 h-5 mx-auto mb-1 opacity-40" />
          No internal notes yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="appt-notes-list">
          {notes.slice().reverse().map((n) => (
            <div key={n.id} className="bg-amber-50/40 border border-amber-100 p-3 flex justify-between items-start gap-2" data-testid={`appt-note-${n.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#282828] whitespace-pre-wrap break-words">{n.note}</div>
                <div className="text-[10px] text-[#676664]/60 mt-1">by {n.admin_email || "admin"} · {fullTime(n.created_at)}</div>
              </div>
              <button onClick={() => remove(n.id)} disabled={deletingId === n.id} className="text-[#676664]/40 hover:text-red-500 p-1" data-testid={`appt-delete-note-${n.id}`}>
                {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityTab({ apptId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/appointments/${apptId}/activity`, { headers: auth() });
        if (!cancelled) setEvents(r.data.events || []);
      } catch { if (!cancelled) toast.error("Could not load activity"); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [apptId]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" /></div>;
  if (!events.length) return <div className="text-center py-8 text-[12px] text-[#676664]/50 italic">No activity yet.</div>;

  return (
    <div className="space-y-2" data-testid="appt-activity-list">
      {events.map((e, idx) => (
        <div key={idx} className="border-l-2 border-[#9A7B4F]/30 pl-3 py-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-[#282828]">{(e.type || "event").replace(/_/g, " ")}</span>
            <span className="text-[10px] text-[#676664]/50">{fullTime(e.timestamp)}</span>
          </div>
          <div className="text-[10px] text-[#676664]/60 mt-0.5">by {e.actor || "system"}</div>
          {e.before && e.after && (
            <div className="text-[10px] text-[#676664]/70 mt-1 font-mono">
              {Object.keys(e.after).map(k => (
                <div key={k}>{k}: <span className="line-through opacity-60">{String(e.before[k] ?? "—")}</span> → <span className="text-[#9A7B4F]">{String(e.after[k] ?? "—")}</span></div>
              ))}
            </div>
          )}
          {e.details?.note && <div className="text-[11px] text-[#5A5A5A] mt-1 italic">"{e.details.note}"</div>}
        </div>
      ))}
    </div>
  );
}

export function AdminAppointmentDetailModal({ appointmentId, onClose, onChanged }) {
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("details");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [smsModal, setSmsModal] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await axios.get(`${API}/admin/appointments/${appointmentId}`, { headers: auth() });
      setAppt(r.data);
    } catch (err) {
      // Surface the actual backend failure so admins (and integrators) can diagnose
      // route-not-registered, auth-expired, schema-incompat, etc. in production.
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
                : detail?.message ? detail.message
                : err.message || "Network error";
      const shown = status ? `HTTP ${status}: ${msg}` : msg;
      console.error("[AdminAppointmentDetailModal] load failed", { appointmentId, status, detail, err });
      setLoadError(shown);
      toast.error(`Could not load appointment — ${shown}`);
    }
    setLoading(false);
  };

  useEffect(() => { if (appointmentId) refresh(); /* eslint-disable-next-line */ }, [appointmentId]);

  const save = async (form) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/appointments/${appointmentId}`, form, { headers: auth() });
      toast.success("Appointment updated");
      setEditing(false);
      await refresh();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "object") {
        toast.error(detail.message || "Time slot conflicts with existing booking");
      } else {
        toast.error(typeof detail === "string" ? detail : "Update failed");
      }
    }
    setSaving(false);
  };

  const softCancel = async () => {
    if (!window.confirm("Cancel this appointment? It will be marked cancelled but kept in records.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}`, { headers: auth() });
      toast.success("Appointment cancelled");
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
    setBusy(false);
  };

  const hardDelete = async () => {
    if (!window.confirm("Permanently DELETE this appointment? This cannot be undone.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}?hard=true`, { headers: auth() });
      toast.success("Appointment deleted");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="appt-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-[#282828]/8">
          <div>
            <h2 className="text-[15px] text-[#282828] font-medium tracking-wide" data-testid="appt-detail-title">Appointment</h2>
            {appt && (
              <div className="flex items-center gap-2 mt-1">
                <StatusPill status={appt.status} />
                <span className="text-[11px] text-[#676664]/60">{(appt.appointment_type || "").replace("_", " ")}</span>
                {appt.source === "admin_manual" && <span className="text-[9px] px-1.5 py-0.5 bg-[#9A7B4F]/10 text-[#9A7B4F] tracking-wider uppercase">Manual</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#676664]/40 hover:text-[#282828] p-1" data-testid="appt-detail-close"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex justify-center items-center p-10"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
        ) : loadError ? (
          <div className="flex-1 flex flex-col justify-center items-center p-10 text-center gap-3" data-testid="appt-detail-error">
            <AlertTriangle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
            <div className="max-w-md">
              <p className="text-[13px] text-[#282828] font-medium mb-1">Could not load this appointment</p>
              <p className="text-[11px] text-[#676664]/70 break-words" data-testid="appt-detail-error-msg">{loadError}</p>
              <p className="text-[10px] text-[#676664]/50 mt-2 font-mono">id: {appointmentId}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="text-[11px] h-8 px-4" data-testid="appt-detail-error-close">Close</Button>
              <Button onClick={refresh} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="appt-detail-retry">Retry</Button>
            </div>
          </div>
        ) : !appt ? null : (
          <>
            <div className="flex gap-1 px-5 border-b border-[#282828]/8">
              {[
                { key: "details", label: "Details" },
                { key: "timeline", label: "Timeline" },
                { key: "activity", label: "Activity" },
                { key: "notes", label: `Notes${(appt.admin_notes || []).length ? ` (${appt.admin_notes.length})` : ""}` },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-[11px] tracking-wider transition-colors border-b-2 -mb-[1px] ${tab === t.key ? 'border-[#9A7B4F] text-[#9A7B4F] font-medium' : 'border-transparent text-[#676664]/50 hover:text-[#676664]'}`}
                  data-testid={`appt-tab-${t.key}`}>{t.label}</button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {tab === "details" && !editing && (
                <div className="space-y-3">
                  {/* Section 8 — closed-outcome summary rendered above the detail grid */}
                  {appt.outcome && <OutcomeSummary appointment={appt} />}
                  <div className="grid grid-cols-2 gap-3 text-[12px]">
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Date</span><Calendar className="w-3 h-3 inline mr-1 text-[#9A7B4F]" />{appt.date}</div>
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Time</span><Clock className="w-3 h-3 inline mr-1 text-[#9A7B4F]" />{formatTime(appt.time)}{appt.duration_minutes ? ` (${appt.duration_minutes} min)` : ""}</div>
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Customer</span>{appt.customer_name || "—"}</div>
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Email</span><Mail className="w-3 h-3 inline mr-1 text-[#676664]/60" />{appt.customer_email || "—"}</div>
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Phone</span><Phone className="w-3 h-3 inline mr-1 text-[#676664]/60" />{appt.customer_phone || "—"}</div>
                    <div><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Source</span>{appt.source || "checkout"}</div>
                    {appt.delivery_address && (
                      <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Delivery address</span><MapPin className="w-3 h-3 inline mr-1 text-[#676664]/60" />{appt.delivery_address}</div>
                    )}
                    {appt.notes && (
                      <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-0.5">Notes</span><p className="bg-[#FAF8F0]/50 border border-[#282828]/6 p-2 text-[11px] whitespace-pre-wrap">{appt.notes}</p></div>
                    )}
                    {appt.cart_group_id && (
                      <div className="col-span-2 text-[10px] text-[#676664]/40 font-mono">Cart group: {appt.cart_group_id}</div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-3 border-t border-[#282828]/8 flex-wrap">
                    {appt.customer_phone && (
                      <Button variant="outline" onClick={() => setSmsModal({ to_phone: appt.customer_phone, customer_name: appt.customer_name, appointment_id: appt.id, customer_id: appt.customer_id, booking_id: (appt.booking_ids && appt.booking_ids[0]) || "" })}
                        className="text-[11px] h-8 px-3 text-[#9A7B4F] border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5"
                        data-testid="appt-send-sms-btn">
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Send SMS
                      </Button>
                    )}
                    {/* Section 8 — trial closure entry-point */}
                    {(appt.appointment_type || "").toLowerCase() === "trial" && (
                      <Button
                        variant="outline"
                        onClick={() => setShowCloseModal(true)}
                        className="text-[11px] h-8 px-3 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        data-testid="appt-close-outcome-btn"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        {appt.outcome ? "Update outcome" : "Close appointment / Mark outcome"}
                      </Button>
                    )}
                    {appt.status !== "cancelled" && (
                      <Button variant="outline" onClick={softCancel} disabled={busy} className="text-[11px] h-8 px-3 text-red-600 border-red-200 hover:bg-red-50" data-testid="appt-cancel-btn">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Cancel appointment
                      </Button>
                    )}
                    <Button variant="outline" onClick={hardDelete} disabled={busy} className="text-[11px] h-8 px-3 text-red-700 border-red-300 hover:bg-red-50" data-testid="appt-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete permanently
                    </Button>
                    <Button onClick={() => setEditing(true)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="appt-edit-btn">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              )}

              {tab === "details" && editing && (
                <EditPanel appt={appt} onSave={save} onCancel={() => setEditing(false)} saving={saving} />
              )}

              {tab === "timeline" && (
                <Timeline
                  appointmentId={appointmentId}
                  title="Communication & Activity Timeline"
                />
              )}
              {tab === "activity" && <ActivityTab apptId={appointmentId} />}
              {tab === "notes" && <NotesTab appt={appt} onChanged={refresh} />}
            </div>
          </>
        )}
      </div>

      {smsModal && (
        <SmsReplyModal
          to={smsModal}
          title="Send SMS to customer"
          template_key="admin_direct_appointment_sms"
          onClose={() => setSmsModal(null)}
          onSent={() => setSmsModal(null)}
        />
      )}

      {/* Section 8 — Close appointment / mark outcome */}
      {showCloseModal && appt && (
        <CloseTrialModal
          appointment={appt}
          onClose={() => setShowCloseModal(false)}
          onClosed={async () => { await refresh(); onChanged?.(); }}
        />
      )}
    </div>
  );
}
